import { difference, isEqual } from 'lodash';
import {
  TagConfig,
  TagContainer,
  TagLabel,
  WaveTag,
  DelineationLabel,
} from '../types/tag.type';
import { InvalidWaveType, Wave, WaveType } from '../types/wave.type';
import { invalidTypes } from './wave.util';

export const DELINEATION_LABELS: Record<DelineationLabel, TagConfig> = {
  ABERRATION: {
    name: 'Aberration',
    priority: 15,
    short: 'abe',
    symbol: '',
    tags: {
      QRS: ['aberration'],
    },
  },
  ATRIAL_FIBRILLATION: {
    name: 'Atrial fibrillation',
    priority: 5,
    short: 'AFIB',
    symbol: '',
    tags: {
      QRS: ['afib'],
      uncertain: ['afib_noise'],
    },
  },
  ATRIAL_FIBRILLATION_FLUTTER: {
    name: 'AFib / Flutter',
    priority: 5,
    short: 'AF',
    symbol: '',
    tags: {
      uncertain: ['afib_noise'],
    },
  },
  ATRIAL_ESCAPE: {
    name: 'Atrial escape',
    priority: 11,
    short: 'e',
    symbol: '',
    tags: {
      P: ['escape', 'ectopic'],
      QRS: ['escape'],
    },
  },
  ATRIAL_PACED: {
    name: 'Atrial paced',
    priority: 8,
    short: '/a',
    symbol: '',
    tags: {
      P: ['paced', 'ectopic'],
      QRS: ['paced'],
    },
  },
  ATRIAL_FLUTTER: {
    name: 'Atrial flutter',
    priority: 6,
    short: 'AFL',
    symbol: '',
    tags: {
      QRS: ['flutter'],
    },
  },
  FUSION: {
    name: 'Fusion',
    priority: 0,
    short: 'fus',
    symbol: '',
    tags: {
      QRS: ['fusion'],
    },
  },
  JUNCTIONAL_ESCAPE: {
    name: 'Junctional escape',
    priority: 9,
    short: 'j',
    symbol: '',
    tags: {
      QRS: ['junctional', 'escape'],
    },
  },
  NOISY_RR: {
    name: 'Noisy RR',
    priority: 15,
    short: 'noisy_rr',
    symbol: '',
    tags: {
      P: ['noisy_rr'],
      QRS: ['noisy_rr'],
      T: ['noisy_rr'],
      artifact: ['noisy_rr'],
      calibration: ['noisy_rr'],
      noise: ['noisy_rr'],
    },
  },
  NOT_CONDUCTED: {
    name: 'Non-conducted',
    priority: 17,
    short: 'non',
    symbol: '',
    tags: {
      P: ['non-conducted'],
    },
  },
  PAC: {
    name: 'PAC',
    priority: 1,
    short: 'A',
    symbol: '',
    tags: {
      P: ['premature', 'ectopic'],
      QRS: ['premature'],
    },
  },
  PJC: {
    name: 'PJC',
    priority: 3,
    short: 'J',
    symbol: '',
    tags: {
      QRS: ['premature', 'junctional'],
    },
  },
  PRE_EXCITATION: {
    name: 'Pre-excitation',
    priority: 110,
    short: 'p-e',
    symbol: '',
    tags: {
      QRS: ['pre-excitation'],
    },
  },
  PVC: {
    name: 'PVC',
    priority: 2,
    short: 'V',
    symbol: '',
    tags: {
      QRS: ['premature', 'ectopic'],
      uncertain: ['pvc'],
    },
  },
  RETROGRADE: {
    name: 'Retrograde',
    priority: 100,
    short: 'ret',
    symbol: '',
    tags: {
      P: ['retrograde'],
    },
  },
  SINUS: {
    name: 'Sinus',
    priority: 0,
    short: 'N',
    symbol: '',
    tags: {
      P: ['sinus'],
      QRS: ['sinus'],
      uncertain: ['sinus_noise'],
    },
  },
  SVE: {
    name: 'SVE',
    priority: 4,
    short: 'S',
    symbol: '',
    tags: {
      P: ['ectopic', 'sve'],
      QRS: ['sve'],
    },
  },
  VENTRICULAR_ESCAPE: {
    name: 'Ventricular escape',
    priority: 10,
    short: 'E',
    symbol: '',
    tags: {
      QRS: ['escape', 'ectopic'],
    },
  },
  VENTRICULAR_PACED: {
    name: 'Ventricular paced',
    priority: 7,
    short: '/v',
    symbol: '',
    tags: {
      QRS: ['paced', 'ectopic'],
    },
  },
  // Noise tags
  P_LEVEL_NOISE: {
    name: 'P-level noise',
    priority: 12,
    short: 'p_level_noise',
    symbol: '',
    tags: {
      artifact: ['p_level_noise'],
      calibration: ['p_level_noise'],
      noise: ['p_level_noise'],
      no_signal: ['p_level_noise'],
      p_level_noise: ['p_level_noise'],
      qrs_level_noise: ['p_level_noise'],
      pt_level_noise: ['p_level_noise'],
      uncertain: ['p_level_noise'],
      microvolted: ['p_level_noise'],
    },
  },
  PT_LEVEL_NOISE: {
    name: 'PT-level noise',
    priority: 13,
    short: 'pt_level_noise',
    symbol: '',
    tags: {
      artifact: ['pt_level_noise'],
      calibration: ['pt_level_noise'],
      noise: ['pt_level_noise'],
      no_signal: ['pt_level_noise'],
      p_level_noise: ['pt_level_noise'],
      qrs_level_noise: ['pt_level_noise'],
      pt_level_noise: ['pt_level_noise'],
      uncertain: ['pt_level_noise'],
      microvolted: ['pt_level_noise'],
    },
  },
  QRS_LEVEL_NOISE: {
    name: 'QRS-level noise',
    priority: 14,
    short: 'qrs_level_noise',
    symbol: '',
    tags: {
      artifact: ['qrs_level_noise'],
      calibration: ['qrs_level_noise'],
      noise: ['qrs_level_noise'],
      no_signal: ['qrs_level_noise'],
      p_level_noise: ['qrs_level_noise'],
      qrs_level_noise: ['qrs_level_noise'],
      pt_level_noise: ['qrs_level_noise'],
      uncertain: ['qrs_level_noise'],
      microvolted: ['qrs_level_noise'],
    },
  },
  NOISE: {
    name: 'Pure noise',
    priority: 15,
    short: 'noise',
    symbol: '',
    tags: {
      artifact: ['noise'],
      calibration: ['noise'],
      noise: ['noise'],
      no_signal: ['noise'],
      p_level_noise: ['noise'],
      qrs_level_noise: ['noise'],
      pt_level_noise: ['noise'],
      uncertain: ['noise'],
      microvolted: ['noise'],
    },
  },
  NO_SIGNAL: {
    name: 'No signal',
    priority: 16,
    short: 'no_signal',
    symbol: '',
    tags: {
      artifact: ['no_signal'],
      calibration: ['no_signal'],
      noise: ['no_signal'],
      no_signal: ['no_signal'],
      p_level_noise: ['no_signal'],
      qrs_level_noise: ['no_signal'],
      pt_level_noise: ['no_signal'],
      uncertain: ['no_signal'],
      microvolted: ['no_signal'],
    },
  },
  CALIBRATION: {
    name: 'Calibration',
    priority: 17,
    short: 'calib',
    symbol: '',
    tags: {
      artifact: ['calibration'],
      calibration: ['calibration'],
      noise: ['calibration'],
      no_signal: ['calibration'],
      p_level_noise: ['calibration'],
      qrs_level_noise: ['calibration'],
      pt_level_noise: ['calibration'],
      uncertain: ['calibration'],
      microvolted: ['calibration'],
    },
  },
  MICROVOLTED: {
    name: 'Microvolted',
    priority: 18,
    short: 'microvolted',
    symbol: '',
    tags: {
      artifact: ['microvolted'],
      calibration: ['microvolted'],
      noise: ['microvolted'],
      no_signal: ['microvolted'],
      p_level_noise: ['microvolted'],
      qrs_level_noise: ['microvolted'],
      pt_level_noise: ['microvolted'],
      uncertain: ['microvolted'],
      microvolted: ['microvolted'],
    },
  },
  UNCERTAIN: {
    name: 'Uncertain',
    priority: 19,
    short: 'uncertain',
    symbol: '',
    tags: {
      artifact: ['uncertain'],
      calibration: ['uncertain'],
      noise: ['uncertain'],
      no_signal: ['uncertain'],
      p_level_noise: ['uncertain'],
      qrs_level_noise: ['uncertain'],
      pt_level_noise: ['uncertain'],
      uncertain: ['uncertain'],
      microvolted: ['uncertain'],
    },
  },
  ARTIFACT: {
    name: 'Artifact',
    priority: 20,
    short: 'artif',
    symbol: '',
    tags: {
      artifact: ['artifact'],
      calibration: ['artifact'],
      noise: ['artifact'],
      no_signal: ['artifact'],
      p_level_noise: ['artifact'],
      qrs_level_noise: ['artifact'],
      pt_level_noise: ['artifact'],
      uncertain: ['artifact'],
      microvolted: ['artifact'],
    },
  },

  UNANALYZABLE_P: {
    name: 'Unanalyzable P',
    priority: 20,
    short: 'un-P',
    symbol: '',
    tags: {
      microvolted: ['unanalyzable_p'],
    },
  },
  P: {
    name: 'P',
    priority: 20,
    short: 'P',
    symbol: '',
    tags: {
      microvolted: ['p'],
    },
  },
  QRS: {
    name: 'QRS',
    priority: 20,
    short: 'QRS',
    symbol: '',
    tags: {
      microvolted: ['qrs'],
    },
  },
  T: {
    name: 'T',
    priority: 20,
    short: 'T',
    symbol: '',
    tags: {
      microvolted: ['t'],
    },
  },
  ADDITIVE: {
    name: 'Additive',
    priority: 21,
    short: 'additive',
    symbol: '',
    tags: {
      p_level_noise: ['additive'],
      qrs_level_noise: ['additive'],
      pt_level_noise: ['additive'],
    },
  },
  VT: {
    name: 'VT',
    priority: 22,
    short: 'vt',
    symbol: '',
    tags: {
      uncertain: ['vt'],
    },
  },
  PAUSE: {
    name: 'Pause',
    priority: 23,
    short: 'pause',
    symbol: '',
    tags: {
      uncertain: ['pause'],
    },
  },
  AVB: {
    name: 'AVB',
    priority: 24,
    short: 'avb',
    symbol: '',
    tags: {
      uncertain: ['avb'],
    },
  },
  SVT: {
    name: 'SVT',
    priority: 25,
    short: 'svt',
    symbol: '',
    tags: {
      uncertain: ['svt'],
    },
  },
  PSVC: {
    name: 'PSVC',
    priority: 26,
    short: 'psvc',
    symbol: '',
    tags: {
      uncertain: ['psvc'],
    },
  },
};

export const requiredTagSet = new Set<TagLabel>([
  'sinus',
  'ectopic',
  'paced',
  'premature',
  'retrograde',
  'junctional',
  'escape',
  'afib',
  'flutter',
  'sve',
  'artifact',
  'calibration',
  'noise',
  'no_signal',
  'p_level_noise',
  'qrs_level_noise',
  'pt_level_noise',
  'uncertain',
  'microvolted',
]);

/**
 * This is where the tag set for the associative tag container will be created
 * here we add all tags that we want to be separate from required and optional tags
 */
export const associativeTagSet = new Set<TagLabel>(['noisy_rr']);

export const optionalTagSet = new Set<TagLabel>([
  'aberration',
  'fusion',
  'non-conducted',
  'pre-excitation',
  'unanalyzable_p',
  'p',
  'qrs',
  't',
  'additive',
  'vt',
  'pause',
  'avb',
  'afib_noise',
  'svt',
  'sinus_noise',
  'pvc',
  'psvc',
]);

export const DELINEATION_NOISE_TAGS: TagConfig[] = [
  DELINEATION_LABELS.P_LEVEL_NOISE,
  DELINEATION_LABELS.PT_LEVEL_NOISE,
  DELINEATION_LABELS.QRS_LEVEL_NOISE,
  DELINEATION_LABELS.NOISE,
  DELINEATION_LABELS.NO_SIGNAL,
  DELINEATION_LABELS.CALIBRATION,
  DELINEATION_LABELS.MICROVOLTED,
  DELINEATION_LABELS.UNCERTAIN,
  DELINEATION_LABELS.ARTIFACT,
];

const TagConfigsByType: Record<WaveType, TagConfig[]> = {
  P: [
    DELINEATION_LABELS.SINUS,
    DELINEATION_LABELS.ATRIAL_PACED,
    DELINEATION_LABELS.PAC,
    DELINEATION_LABELS.SVE,
    DELINEATION_LABELS.ATRIAL_ESCAPE,
    DELINEATION_LABELS.RETROGRADE,
  ],
  QRS: [
    DELINEATION_LABELS.SINUS,
    DELINEATION_LABELS.PVC,
    DELINEATION_LABELS.VENTRICULAR_ESCAPE,
    DELINEATION_LABELS.ATRIAL_PACED,
    DELINEATION_LABELS.VENTRICULAR_PACED,
    DELINEATION_LABELS.PAC,
    DELINEATION_LABELS.SVE,
    DELINEATION_LABELS.ATRIAL_ESCAPE,
    DELINEATION_LABELS.PJC,
    DELINEATION_LABELS.JUNCTIONAL_ESCAPE,
    DELINEATION_LABELS.ATRIAL_FIBRILLATION,
    DELINEATION_LABELS.ATRIAL_FLUTTER,
  ],
  T: [],
  calibration: DELINEATION_NOISE_TAGS,
  artifact: DELINEATION_NOISE_TAGS,
  noise: DELINEATION_NOISE_TAGS,
  no_signal: DELINEATION_NOISE_TAGS,
  p_level_noise: DELINEATION_NOISE_TAGS,
  qrs_level_noise: DELINEATION_NOISE_TAGS,
  pt_level_noise: DELINEATION_NOISE_TAGS,
  uncertain: DELINEATION_NOISE_TAGS,
  microvolted: DELINEATION_NOISE_TAGS,
};

const getDelineationTagFromConfig = (
  a: TagConfig,
  type: WaveType,
): TagContainer => {
  return {
    name: a.name,
    priority: a.priority,
    short: a.short,
    symbol: a.symbol,
    tags: a.tags[type] || [],
  };
};

export const getAllowedTagContainers = (
  allowedTags: WaveTag,
  type: WaveType,
) => {
  if (allowedTags[type] == null) {
    return [];
  }

  return (allowedTags[type] as DelineationLabel[])
    .filter(
      (tag) =>
        !associativeTagSet.has(tag.toLowerCase()) &&
        DELINEATION_LABELS[tag] != null &&
        difference(
          DELINEATION_LABELS[tag]?.tags[type],
          Array.from(optionalTagSet),
        ).length !== 0,
    )
    .map((tag) => getDelineationTagFromConfig(DELINEATION_LABELS[tag], type));
};

const getTagContainerForTypeFromConfig = (
  arr: TagConfig[] = [],
  type: WaveType,
): TagContainer[] => {
  return arr
    .map((tagConfig) => getDelineationTagFromConfig(tagConfig, type))
    .sort(({ priority: p1 }, { priority: p2 }) => p1 - p2);
};

export const getTagContainers = (
  type: WaveType,
  config = TagConfigsByType,
): TagContainer[] => {
  return getTagContainerForTypeFromConfig(config[type], type);
};

export const getTagContainerFromTags = (
  type: WaveType,
  tags: TagLabel[] = [],
): TagContainer | null => {
  const tagContainers = getTagContainers(type);

  for (const tagContainer of tagContainers) {
    if (isTagsEquals(tags, tagContainer.tags)) {
      return tagContainer;
    }
  }

  return null;
};

const excludeManual = (name: TagLabel): boolean => {
  return name !== 'manual';
};

const isRequired = (name: TagLabel): boolean => {
  return requiredTagSet.has(name);
};

export const isOptional = (name: TagLabel): boolean => {
  return optionalTagSet.has(name);
};

const isAssociative = (name: TagLabel): boolean => {
  return associativeTagSet.has(name);
};

export const isTagsEquals = (a: TagLabel[], b: TagLabel[]): boolean => {
  return isEqual(
    a.filter(excludeManual).sort(),
    b.filter(excludeManual).sort(),
  );
};

export const getRequiredTags = (tags: TagLabel[]): TagLabel[] => {
  return tags.filter(isRequired);
};

export const getOptionalTags = (tags: TagLabel[]): TagLabel[] => {
  return tags.filter(isOptional);
};

export const getAssociativeTags = (tags: TagLabel[]): TagLabel[] => {
  return tags.filter(isAssociative);
};

export const getExtraTags = (tags: TagLabel[]): TagLabel[] => {
  return tags.filter(
    (tag) => !isRequired(tag) && !isOptional(tag) && !isAssociative(tag),
  );
};

export const getOptionalTagContainers = (
  waveType: WaveType,
  waveTags: TagLabel[],
): TagContainer[] => {
  const requiredTags = getRequiredTags(waveTags);
  const currentTagContainer = getTagContainerFromTags(waveType, requiredTags);

  if (currentTagContainer == null) {
    return [];
  }

  if (waveType === 'P') {
    const currName = currentTagContainer.name;
    if (currName === DELINEATION_LABELS.RETROGRADE.name) {
      return [];
    }

    return getTagContainerForTypeFromConfig(
      [DELINEATION_LABELS.NOT_CONDUCTED],
      'P',
    );
  } else if (invalidTypes.includes(waveType as InvalidWaveType)) {
    const tags = [];
    const currName = currentTagContainer.name;

    if (currName === DELINEATION_LABELS.MICROVOLTED.name) {
      tags.push(DELINEATION_LABELS.UNANALYZABLE_P);
      tags.push(DELINEATION_LABELS.P);
      tags.push(DELINEATION_LABELS.QRS);
      tags.push(DELINEATION_LABELS.T);
    }

    if (currName === DELINEATION_LABELS.UNCERTAIN.name) {
      tags.push(DELINEATION_LABELS.VT);
      tags.push(DELINEATION_LABELS.PAUSE);
      tags.push(DELINEATION_LABELS.AVB);
      tags.push(DELINEATION_LABELS.ATRIAL_FIBRILLATION_FLUTTER);
      tags.push(DELINEATION_LABELS.SVT);
      tags.push(DELINEATION_LABELS.SINUS);
      tags.push(DELINEATION_LABELS.PVC);
      tags.push(DELINEATION_LABELS.PSVC);
    }

    return getTagContainerForTypeFromConfig(tags, waveType);
  } else if (waveType === 'QRS') {
    const tags = [];
    const currName = currentTagContainer.name;

    if (
      currName === DELINEATION_LABELS.SINUS.name ||
      currName === DELINEATION_LABELS.ATRIAL_PACED.name ||
      currName === DELINEATION_LABELS.PAC.name ||
      currName === DELINEATION_LABELS.SVE.name ||
      currName === DELINEATION_LABELS.ATRIAL_ESCAPE.name ||
      currName === DELINEATION_LABELS.ATRIAL_FIBRILLATION.name ||
      currName === DELINEATION_LABELS.ATRIAL_FLUTTER.name
    ) {
      tags.push(DELINEATION_LABELS.PRE_EXCITATION);
    }

    if (
      currName === DELINEATION_LABELS.PVC.name ||
      currName === DELINEATION_LABELS.VENTRICULAR_ESCAPE.name ||
      currName === DELINEATION_LABELS.VENTRICULAR_PACED.name
    ) {
      tags.push(DELINEATION_LABELS.FUSION);
    }

    if (
      currName === DELINEATION_LABELS.ATRIAL_PACED.name ||
      currName === DELINEATION_LABELS.PAC.name ||
      currName === DELINEATION_LABELS.PJC.name ||
      currName === DELINEATION_LABELS.ATRIAL_FIBRILLATION.name ||
      currName === DELINEATION_LABELS.ATRIAL_FLUTTER.name ||
      currName === DELINEATION_LABELS.SVE.name
    ) {
      tags.push(DELINEATION_LABELS.ABERRATION);
    }

    return getTagContainerForTypeFromConfig(tags, waveType);
  }

  return [];
};

// This will produce a third tag container that will contain values that do not
// exist in the required tags or optional tags and will be applied everywhere
// The container for these tags will be created here and new tags that will have the same functionality will be
// added here
export const getAssociativeTagContainersFromTags = (
  waveType: WaveType,
): TagContainer[] => {
  const tags = [];
  if (waveType === 'QRS') {
    tags.push(DELINEATION_LABELS.NOISY_RR);
  }

  return getTagContainerForTypeFromConfig(tags, waveType);
};

export const getAssociativeTagContainers = (
  allowedTags: WaveTag,
  waveType: WaveType,
): TagContainer[] => {
  if (allowedTags[waveType] == null) {
    return [];
  }

  const tags = (allowedTags[waveType] as DelineationLabel[])
    .filter(
      (tag) =>
        associativeTagSet.has(tag.toLowerCase()) &&
        DELINEATION_LABELS[tag] != null,
    )
    .map((tag) => DELINEATION_LABELS[tag]);

  return getTagContainerForTypeFromConfig(tags, waveType);
};

export const isTagContainersEqual = (
  tags: string[],
  waveTags: string[],
  tagSet: Set<string>[],
) => {
  const newTags = waveTags.filter(
    (tag) => tagSet.every((tagSet) => !tagSet.has(tag)) && tag !== 'manual',
  );

  if (
    tags.includes('premature') &&
    tags.length === 1 &&
    newTags.includes('premature') &&
    newTags.length > 1
  ) {
    return false;
  }

  return difference(tags, newTags).length === 0;
};

export const getTagLabel = (wave: Wave): string => {
  const requiredTags = getRequiredTags(wave.tags);
  const optionalTags = getOptionalTags(wave.tags);
  const associativeTags = getAssociativeTags(wave.tags);

  const requiredTagContainers = getTagContainerFromTags(
    wave.type,
    requiredTags,
  );
  const optionalTagContainers = getOptionalTagContainers(
    wave.type,
    wave.tags,
  ).filter(
    (tagContainer) =>
      difference(optionalTags, tagContainer.tags).length !==
      optionalTags.length,
  );

  const associativeTagContainers = getAssociativeTagContainersFromTags(
    wave.type,
  ).filter(
    (tagContainer) =>
      difference(associativeTags, tagContainer.tags).length !==
      associativeTags.length,
  );

  let label: string[] = [];
  if (requiredTagContainers != null) {
    label.push(requiredTagContainers.short);
  }

  if (optionalTagContainers.length > 0) {
    label = label.concat(optionalTagContainers.map((a) => a.short));
  }

  if (associativeTagContainers.length > 0) {
    label = label.concat(associativeTagContainers.map((a) => a.short));
  }

  if (label.length === 0) {
    // For retro-compatibility, show tags that do not match any expected tags.
    const shortTags = wave.tags
      .filter(excludeManual)
      .map((tag) => tag.slice(0, 3));
    label = label.concat(shortTags);
  }

  return label.join(' ');
};

export const getOptionalTagLabel = (wave: Wave): string => {
  const selectedOptionalTags = getOptionalTags(wave.tags);
  if (selectedOptionalTags.length === 0) return '';
  const selectedOptionalTagsSet = new Set(selectedOptionalTags);

  return getOptionalTagContainers(wave.type, wave.tags)
    .filter((tagContainer) =>
      tagContainer.tags.some((tag) => selectedOptionalTagsSet.has(tag)),
    )
    .map((tagContainer) => tagContainer.short)
    .join(' ');
};
